import { useCallback, useRef } from 'react';

import styles from './Scrubber.module.css';

export interface ScrubberProps {
  positionMs: number;
  durationMs: number;
  onSeek: (ms: number) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

function clamp(value: number, limit: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(limit, value));
}

export function Scrubber({
  positionMs,
  durationMs,
  onSeek,
  disabled = false,
  ariaLabel = 'Перемотка',
}: ScrubberProps) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const known = durationMs > 0;
  const percent = known ? clamp(positionMs, durationMs) / durationMs : 0;

  const seekFromPointer = useCallback(
    (clientX: number) => {
      const rail = railRef.current;
      if (!rail || !known) return;
      const box = rail.getBoundingClientRect();
      if (box.width <= 0) return;
      const share = clamp(clientX - box.left, box.width) / box.width;
      onSeek(Math.round(share * durationMs));
    },
    [durationMs, known, onSeek],
  );

  const step = (delta: number) => {
    if (!known) return;
    onSeek(clamp(positionMs + delta, durationMs));
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || !known) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-5000);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(5000);
    } else if (event.key === 'Home') {
      event.preventDefault();
      onSeek(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      onSeek(durationMs);
    }
  };

  return (
    <div
      ref={railRef}
      className={disabled || !known ? styles.railDisabled : styles.rail}
      role="slider"
      tabIndex={disabled || !known ? -1 : 0}
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={Math.round(durationMs)}
      aria-valuenow={Math.round(clamp(positionMs, durationMs))}
      aria-disabled={disabled || !known}
      onKeyDown={onKeyDown}
      onPointerDown={(event) => {
        if (disabled || !known) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        seekFromPointer(event.clientX);
      }}
      onPointerMove={(event) => {
        if (disabled || !known) return;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          seekFromPointer(event.clientX);
        }
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
    >
      <div className={styles.fill} style={{ width: `${percent * 100}%` }} />
      <div className={styles.knob} style={{ left: `${percent * 100}%` }} />
    </div>
  );
}
